import { faArrowsUpDownLeftRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Checkbox, notification, Spin } from "antd";
import React from "react";
import GlobalStoreContext from "../../../context/GlobalStoreContext";
import { downloadBlob, pdfFileName } from "../../PDF/Shared/pdfFiles";
import PdfExportDialogShell, { PdfOptionGroup } from "../../PDF/Shared/PdfExportDialogShell";
import PdfMapCapture from "../../PDF/Shared/PdfMapCapture";
import PdfMapFrameEditor from "../../PDF/Shared/PdfMapFrameEditor";
import PdfQrCode from "../../PDF/Shared/PdfQrCode";
import { PdfMapCamera, zoomForWidth } from "../../PDF/Shared/PdfMapView";
import { useRenderedPdf } from "../../PDF/Shared/useRenderedPdf";
import { buildDamnPdfData, damnPdfLiveUrl, DamnPdfSources } from "./damnPdfData";
import { DamnPdfData } from "./damnPdfDocument";
import {
    DAMN_PDF_CAMERA_WIDTH,
    DAMN_PDF_MAP_FRAMES,
    DAMN_PDF_MAP_LABELS,
    damnPdfJobsiteLogo,
    damnPdfMapContents,
    DamnPdfMapKind,
    damnPdfMapLayout,
    damnPdfMapStyle,
    damnPdfRoutesShown,
    damnPdfShownMaps,
} from "./damnPdfMaps";
import { DAMN_PDF_OPTION_GROUPS, DamnPdfInclude, loadDamnPdfInclude, saveDamnPdfInclude } from "./damnPdfOptions";
import { renderDamnPdf } from "./damnPdfRender";

interface Props extends Omit<DamnPdfSources, "context"> {
    onClose: () => void;
}

// Long enough for tiles over a slow connection; past it the PDF goes out without that map rather than not at all.
const MAP_CAPTURE_TIMEOUT_MS = 20000;

type Capture = { key: string; image: string | null };

// Mounted only while open (see DamnPdfExportButton), so each opening starts fresh and the maps and details match the
// delivery as it is now.
const DamnPdfExportDialog: React.FC<Props> = ({ onClose, ...sources }) => {
    const context = React.useContext(GlobalStoreContext);
    const [include, setInclude] = React.useState<DamnPdfInclude>(loadDamnPdfInclude);
    const [data, setData] = React.useState<DamnPdfData | null>(null);
    const [qrCode, setQrCode] = React.useState<{ ready: boolean; image: string | null }>({ ready: false, image: null });
    // The latest snapshot of each map, with the key of what it was drawn for (see captureKey).
    const [captures, setCaptures] = React.useState<Partial<Record<DamnPdfMapKind, Capture>>>({});
    // Views framed in the editor, by map, with zooms for a DAMN_PDF_CAMERA_WIDTH-wide map; null fits the map's items.
    const [cameras, setCameras] = React.useState<Record<DamnPdfMapKind, PdfMapCamera | null>>({
        approach: null,
        site: null,
    });
    const [editing, setEditing] = React.useState<DamnPdfMapKind | null>(null);

    const mapProject = sources.mapItems.project || context.currentProjectInfo;
    // Taken apart so the maps only redraw when what they show changes; the map items object itself is new each render.
    const {
        selectedHaulRoute,
        selectedHaulRouteMapObject,
        haulRoutes,
        gateToDropoffRoutes,
        gateMapObject,
        dropOffMapObject,
        gate,
        dropoff,
    } = sources.mapItems;

    // Built on opening, and again if the map items it reads change: the dialog can open before they've settled, e.g.
    // while useDamnMapItems is still fetching an assigned haul route that has since been archived. The maps and QR code
    // are added as they're drawn.
    const buildRequest = React.useRef(0);
    const prepare = React.useEffectEvent(() => {
        const request = ++buildRequest.current;
        buildDamnPdfData({ context, ...sources })
            .then((built) => {
                // A rebuild started since supersedes this one.
                if (request === buildRequest.current) setData(built);
            })
            .catch(() => notification.error({ message: "Couldn't prepare the PDF", description: "Please try again." }));
    });
    React.useEffect(() => {
        prepare();
    }, [
        selectedHaulRoute,
        selectedHaulRouteMapObject,
        haulRoutes,
        gateToDropoffRoutes,
        gateMapObject,
        dropOffMapObject,
        gate,
        dropoff,
    ]);
    const routeAssigned = !!selectedHaulRoute;
    const routesShown = damnPdfRoutesShown(include, routeAssigned);
    const contentsWith = React.useCallback(
        (withRoutes: boolean) =>
            damnPdfMapContents(
                {
                    selectedHaulRoute,
                    selectedHaulRouteMapObject,
                    haulRoutes,
                    gateToDropoffRoutes,
                    gateMapObject,
                    dropOffMapObject,
                },
                context,
                withRoutes
            ),
        [
            selectedHaulRoute,
            selectedHaulRouteMapObject,
            haulRoutes,
            gateMapObject,
            dropOffMapObject,
            gateToDropoffRoutes,
            context,
        ]
    );
    // What each map could show at all (as in the PDF data), and what it draws with the route options as they are.
    const fullContents = React.useMemo(() => contentsWith(true), [contentsWith]);
    const mapContents = React.useMemo(() => contentsWith(routesShown), [contentsWith, routesShown]);

    const shownMaps = React.useMemo(
        () =>
            damnPdfShownMaps(
                { approach: fullContents.approach.fit.length > 0, site: fullContents.site.fit.length > 0 },
                include,
                routeAssigned
            ),
        [fullContents, include, routeAssigned]
    );
    const layout = damnPdfMapLayout(shownMaps);
    const frame = DAMN_PDF_MAP_FRAMES[layout];
    const cameraFor = (kind: DamnPdfMapKind): PdfMapCamera | null => {
        const camera = cameras[kind];
        return camera ? { ...camera, zoom: zoomForWidth(camera.zoom, DAMN_PDF_CAMERA_WIDTH, frame.width) } : null;
    };
    // The jobsite map always draws the GeoTIFF; on the haul route map it's an option.
    const showGeotiff = (kind: DamnPdfMapKind) => kind !== "approach" || include.approachGeotiff;
    // The haul route map always marks the jobsite with the receiving team's logo (the GC's when the GC is receiving),
    // which comes with the PDF data.
    const jobsiteLogo = React.useMemo(
        () => damnPdfJobsiteLogo(mapProject, data?.receivingTeam?.logo),
        [mapProject, data?.receivingTeam?.logo]
    );
    const logoFor = (kind: DamnPdfMapKind) => (kind === "approach" ? jobsiteLogo : null);
    // A map is drawn again whenever what it shows, its framed view or the layout (and so its shape) changes.
    const captureKey = (kind: DamnPdfMapKind) =>
        JSON.stringify([
            kind,
            layout,
            cameras[kind],
            showGeotiff(kind),
            !!logoFor(kind),
            mapContents[kind].draw.map((mapObject) => mapObject.uuid),
        ]);
    // The haul route map waits for the data, so its first snapshot has the logo.
    const pending = shownMaps.filter(
        (kind) => captures[kind]?.key !== captureKey(kind) && (kind !== "approach" || data !== null)
    );
    const capturing = pending.length > 0;
    const pendingSignature = JSON.stringify(pending.map((kind) => [kind, captureKey(kind)]));

    React.useEffect(() => {
        const waiting: [DamnPdfMapKind, string][] = JSON.parse(pendingSignature);
        if (waiting.length === 0) return;
        const timer = window.setTimeout(() => {
            notification.warning({
                message: "Map took too long to load",
                description: "The PDF will be created without it.",
            });
            setCaptures((previous) => ({
                ...previous,
                ...Object.fromEntries(waiting.map(([kind, key]) => [kind, { key, image: null }])),
            }));
        }, MAP_CAPTURE_TIMEOUT_MS);
        return () => window.clearTimeout(timer);
    }, [pendingSignature]);

    // Waits for the data, the QR code and a first snapshot of each map; after that a redrawn map replaces the old one
    // when it's ready.
    const ready = !!data && qrCode.ready && shownMaps.every((kind) => captures[kind] !== undefined);
    const approachImage = captures.approach?.image ?? null;
    const siteImage = captures.site?.image ?? null;
    const pdfData = React.useMemo(
        () =>
            ready && data
                ? {
                      ...data,
                      qrCode: qrCode.image,
                      maps: {
                          approach: { ...data.maps.approach, image: approachImage },
                          site: { ...data.maps.site, image: siteImage },
                      },
                  }
                : null,
        [ready, data, qrCode, approachImage, siteImage]
    );
    // A new object whenever the data or the choices change, which is what re-renders the PDF.
    const renderInput = React.useMemo(() => (pdfData ? { data: pdfData, include } : null), [pdfData, include]);
    const { blob, version, rendering } = useRenderedPdf(renderInput, (input) =>
        renderDamnPdf(input.data, input.include)
    );

    const toggle = (key: keyof DamnPdfInclude, checked: boolean) => {
        const next = { ...include, [key]: checked };
        setInclude(next);
        saveDamnPdfInclude(next);
    };

    const applyMapView = (kind: DamnPdfMapKind, camera: PdfMapCamera | null) => {
        setCameras({
            ...cameras,
            [kind]: camera ? { ...camera, zoom: zoomForWidth(camera.zoom, frame.width, DAMN_PDF_CAMERA_WIDTH) } : null,
        });
        setEditing(null);
    };

    const download = () => {
        if (!blob) return;
        // Jobsite first, like the logistics sheet's, so a folder of downloads groups by site.
        const name = [mapProject?.project_name, sources.calendarEvent.name || "delivery"].filter(Boolean).join(" ");
        downloadBlob(blob, pdfFileName(name, sources.calendarEvent.startAt));
    };

    return (
        <PdfExportDialogShell
            title="Printable delivery instructions"
            preparingLabel={capturing ? "Drawing the maps..." : "Preparing the instructions..."}
            blob={blob}
            version={version}
            rendering={rendering || (capturing && !!blob)}
            onDownload={download}
            onClose={onClose}
            editor={
                editing && shownMaps.includes(editing) ? (
                    <PdfMapFrameEditor
                        key={`${editing}_${layout}`}
                        mapObjects={mapContents[editing].draw}
                        fitTo={mapContents[editing].fit}
                        project={mapProject}
                        mapStyle={damnPdfMapStyle(editing, mapProject)}
                        captureWidth={frame.width}
                        captureHeight={frame.height}
                        padding={frame.padding}
                        camera={cameraFor(editing)}
                        crop={frame.crop}
                        showGeotiff={showGeotiff(editing)}
                        logo={logoFor(editing)}
                        onApply={(camera) => applyMapView(editing, camera)}
                        onCancel={() => setEditing(null)}
                    />
                ) : null
            }
            options={
                pdfData ? (
                    <>
                        {DAMN_PDF_OPTION_GROUPS.map((group) => {
                            const options = group.options.filter((option) => option.available(pdfData));
                            if (options.length === 0) return null;
                            return (
                                <PdfOptionGroup key={group.title} title={group.title}>
                                    {options.map((option) => (
                                        <Checkbox
                                            key={option.key}
                                            checked={include[option.key]}
                                            disabled={
                                                // Steps only show under a route, so they don't apply with both routes
                                                // off.
                                                (option.key === "routeSteps" &&
                                                    !(include.haulRoute && pdfData.haulRoute) &&
                                                    !include.gateToDropoffRoutes) ||
                                                // The approach map shows the haul routes, so it needs them ticked.
                                                (option.key === "approachMap" && !routesShown) ||
                                                // Only applies while the haul route map is printed.
                                                (option.key === "approachGeotiff" && !shownMaps.includes("approach"))
                                            }
                                            onChange={(event) => toggle(option.key, event.target.checked)}
                                        >
                                            {option.label}
                                        </Checkbox>
                                    ))}
                                </PdfOptionGroup>
                            );
                        })}
                        {shownMaps.length > 0 && (
                            <PdfOptionGroup title="Map view">
                                {shownMaps.map((kind) => (
                                    <React.Fragment key={kind}>
                                        <Button
                                            icon={<FontAwesomeIcon icon={faArrowsUpDownLeftRight} />}
                                            disabled={editing !== null}
                                            onClick={() => setEditing(kind)}
                                        >
                                            {DAMN_PDF_MAP_LABELS[kind].adjust}
                                        </Button>
                                        {cameras[kind] && (
                                            <span className="text-[11px] text-gray-500">
                                                Using the view you framed.
                                            </span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </PdfOptionGroup>
                        )}
                    </>
                ) : (
                    <Spin size="small" />
                )
            }
        >
            {pending.map((kind) => {
                const key = captureKey(kind);
                return (
                    <PdfMapCapture
                        key={key}
                        mapObjects={mapContents[kind].draw}
                        fitTo={mapContents[kind].fit}
                        project={mapProject}
                        mapStyle={damnPdfMapStyle(kind, mapProject)}
                        width={frame.width}
                        height={frame.height}
                        padding={frame.padding}
                        camera={cameraFor(kind)}
                        showGeotiff={showGeotiff(kind)}
                        logo={logoFor(kind)}
                        onCapture={(image) => setCaptures((previous) => ({ ...previous, [kind]: { key, image } }))}
                    />
                );
            })}
            {!qrCode.ready && (
                <PdfQrCode
                    value={damnPdfLiveUrl(mapProject, sources.delivery)}
                    onReady={(image) => setQrCode({ ready: true, image })}
                />
            )}
        </PdfExportDialogShell>
    );
};

export default DamnPdfExportDialog;
